/** Pure state machine for the script loader (no DOM, testable in Node). */
export function nextLoaderPhase(current, event) {
    switch (event) {
        case 'start':
            // A re-`start` while already ready is a no-op (already loaded).
            return current === 'ready' ? 'ready' : 'loading';
        case 'loaded':
            return 'ready';
        case 'failed':
            return 'error';
    }
}
/**
 * The client key from the Vite env, or `null` when absent/blank. A missing key
 * is NOT an error: the workforce map falls back to the dependency-free SVG
 * renderer. Only the referrer-restricted client key ever reaches the browser.
 */
export function resolveMapsKey(env) {
    const key = (env.VITE_GOOGLE_MAPS_API_KEY ?? '').trim();
    return key.length > 0 ? key : null;
}
/**
 * The breadcrumb path for a `google.maps.Polyline` — the ACTUAL captured
 * points in capture order. Returns `null` for fewer than two points so we
 * NEVER draw a fabricated line between clock-in and clock-out. Phibian is the
 * source of truth: this feature makes no directions call anywhere.
 */
export function buildRoutePath(points) {
    if (points.length < 2)
        return null;
    return points.map((p) => ({ lat: p.latitude, lng: p.longitude }));
}
/**
 * Worker/cluster/punch/stop markers → SDK-ready specs. Clustering is performed
 * by the caller (the existing dep-free `gridCluster`) and passed through here
 * unchanged; this function only normalises position, clickability and the
 * selected flag.
 */
export function buildMarkerSpecs(markers, selectedId, onSelect) {
    return markers.map((m) => ({
        id: m.id,
        position: { lat: m.latitude, lng: m.longitude },
        kind: m.kind,
        tone: m.tone,
        label: m.label,
        count: m.count,
        clickable: m.kind === 'worker' && onSelect != null,
        selected: m.selected || m.id === selectedId,
    }));
}
/** Geofence circles → SDK-ready specs (the backend stores centre + radius). */
export function buildFenceSpecs(fences) {
    return fences.map((f) => ({
        id: f.id,
        name: f.name,
        center: { lat: f.latitude, lng: f.longitude },
        radiusMeters: f.radiusMeters,
    }));
}
/**
 * The honest, non-alarming label shown above the SVG fallback. Never implies
 * the data is missing or degraded — only the renderer differs. Kept as a pure
 * function (no JSX) so the wording is unit-tested without a DOM.
 */
export function mapFallbackMessage(reason) {
    return reason === 'no-key'
        ? 'Interactive map isn’t configured for this environment — showing locations as a simplified view below. Nothing here is invented; every position is the same data the map would draw.'
        : 'The interactive map couldn’t load — showing locations as a simplified view below. Nothing here is invented; every position is the same data the map would draw.';
}
/** Escape a user-supplied string for safe interpolation into InfoWindow HTML. */
export function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
//# sourceMappingURL=googleMapsLogic.js.map